// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardServices';
import { companyService, userService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  ActivityChart, 
  UserDistributionChart,
  MetricsCard
} from '../../components/dashboard/DashboardCharts';
import {
  UserEngagementChart,
  PerformanceMetricsChart
} from '../../components/dashboard/EnhancedCharts';

// Icons for metric cards
const ICONS = {
  user: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  analysis: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  subscription: "M13 10V3L4 14h7v7l9-11h-7z",
  activity: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
};

const AdminDashboard = () => {
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalUsers: 0,
      totalAnalyses: 0,
      recentActivitiesCount: 0,
      subscriptionDaysRemaining: 0
    },
    historicalMetrics: {
      totalUsers: 0,
      totalAnalyses: 0,
      recentActivitiesCount: 0,
      subscriptionDaysRemaining: 0
    },
    recentActivities: [],
    userDistribution: [],
    activityTrends: [],
    engagementData: [],
    performanceData: [],
    companyInfo: {}
  });
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch provider metrics using the dashboard service
        const response = await dashboardService.getProviderMetrics();
        let metricsData;
        
        if (response.success) {
          metricsData = response.data;
        } else {
          throw new Error('Failed to fetch dashboard metrics');
        }

        // Fetch historical data for trend calculation
        let historicalData = {};
        try {
          const historicalResponse = await dashboardService.getHistoricalMetrics(7);
          if (historicalResponse.success) {
            historicalData = historicalResponse.data.previousPeriod || {};
          }
        } catch (err) {
          console.warn('Historical data not available');
          historicalData = {};
        }

        // Prepare user distribution data for charts
        const userDistribution = [
          { name: 'Admins', value: metricsData.usersByRole?.admin || 0 },
          { name: 'Analystes', value: metricsData.usersByRole?.analyst || metricsData.usersByRole?.simpleuser || 0 },
          { name: 'Consultants', value: metricsData.usersByRole?.viewer || 0 }
        ];
        
        // Use real activity trends data from API
        const activityTrends = metricsData.activityTrends || [];
        
        // Use real user engagement data from API
        const engagementData = metricsData.userEngagement || [];
        
        // Sample performance metrics data for company
        const performanceData = [
          { subject: 'Analyses Complétées', value: 85 },
          { subject: 'Utilisateurs Actifs', value: 70 },
          { subject: 'Conformité EBIOS', value: 90 },
          { subject: 'Temps de Réponse', value: 75 },
          { subject: 'Satisfaction', value: 88 }
        ];
        
        setDashboardData({
          metrics: {
            totalUsers: metricsData.totalUsers || 0,
            totalAnalyses: metricsData.totalAnalyses || metricsData.activeAnalyses || 0,
            recentActivitiesCount: metricsData.recentActivitiesCount || 0,
            subscriptionDaysRemaining: metricsData.subscriptionDaysRemaining || 0
          },
          historicalMetrics: {
            totalUsers: historicalData.totalUsers || 0,
            totalAnalyses: historicalData.totalAnalyses || historicalData.activeAnalyses || 0,
            recentActivitiesCount: historicalData.recentActivitiesCount || 0,
            subscriptionDaysRemaining: historicalData.subscriptionDaysRemaining || 0
          },
          recentActivities: metricsData.recentActivities || [],
          userDistribution,
          activityTrends,
          engagementData,
          performanceData,
          companyInfo: metricsData.companyInfo || {}
        });
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        setError('Erreur lors du chargement des données. Veuillez réessayer plus tard.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Helper function to determine activity badge color
  const getActivityBadgeColor = (actionType) => {
    if (actionType.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('CREATE')) return 'bg-green-100 text-green-800';
    if (actionType.includes('UPDATE')) return 'bg-yellow-100 text-yellow-800';
    if (actionType.includes('DELETE')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Helper function to get subscription status color
  const getSubscriptionStatusColor = (daysRemaining) => {
    if (daysRemaining <= 3) return 'text-red-600';
    if (daysRemaining <= 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }
  
  // Show error message if something went wrong
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm flex-1">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }
  
  const { metrics, historicalMetrics, recentActivities, userDistribution, activityTrends, engagementData, performanceData, companyInfo } = dashboardData;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard Administrateur
          </h1>
          {companyInfo.name && (
            <p className="text-sm text-gray-500 mt-0.5">{companyInfo.name}</p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200">
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Dernière mise à jour : {new Date().toLocaleString()}</span>
            </div>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 bg-white shadow-sm border border-slate-200 transition-colors"
            title="Actualiser les données"
            disabled={isLoading}
          >
            <svg className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Subscription Status Alert */}
      {companyInfo.subscription && companyInfo.subscription.status === 'active' && (
        <div className={`mb-6 p-4 rounded-xl shadow-sm border ${
          metrics.subscriptionDaysRemaining <= 3 
            ? 'bg-red-50 border-red-200' 
            : metrics.subscriptionDaysRemaining <= 7 
              ? 'bg-yellow-50 border-yellow-200' 
              : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-3 rounded-full mr-4 ${
                metrics.subscriptionDaysRemaining <= 3 
                  ? 'bg-red-100' 
                  : metrics.subscriptionDaysRemaining <= 7 
                    ? 'bg-yellow-100' 
                    : 'bg-green-100'
              }`}>
                <svg className={`w-6 h-6 ${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className={`font-bold text-lg ${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)}`}>
                  Statut de l'abonnement
                </h3>
                <p className={`${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)} opacity-80`}>
                  {metrics.subscriptionDaysRemaining} jour(s) restant(s)
                </p>
              </div>
            </div>
            <Link 
              to="/admin/subscription"
              className="bg-white px-4 py-2 rounded-md shadow-sm border border-slate-200 transition-colors font-medium text-sm text-gray-700 hover:text-blue-600 flex items-center space-x-2"
            >
              <span>Gérer l'abonnement</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}
      
      {/* Main Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Utilisateurs</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.totalUsers}</p>
              <p className="text-xs text-gray-500 mt-1">Total des utilisateurs</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.user} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/users"
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
          >
            Gérer les utilisateurs
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Analyses</p>
              <p className="text-2xl font-bold text-green-600">{metrics.totalAnalyses}</p>
              <p className="text-xs text-gray-500 mt-1">Analyses actives</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.analysis} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/analyses"
            className="mt-4 inline-flex items-center text-green-600 hover:text-green-800 font-medium text-sm transition-colors duration-200"
          >
            Voir les analyses
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Activités Récentes</p>
              <p className="text-2xl font-bold text-purple-600">{metrics.recentActivitiesCount}</p>
              <p className="text-xs text-gray-500 mt-1">Dernières actions</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.activity} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/activities"
            className="mt-4 inline-flex items-center text-purple-600 hover:text-purple-800 font-medium text-sm transition-colors duration-200"
          >
            Voir les logs
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Jours d'Abonnement</p>
              <p className={`text-2xl font-bold ${metrics.subscriptionDaysRemaining <= 7 ? 'text-red-600' : 'text-yellow-600'}`}>
                {metrics.subscriptionDaysRemaining}
              </p>
              <p className="text-xs text-gray-500 mt-1">Jours restants</p>
            </div>
            <div className={`p-3 rounded-full ${metrics.subscriptionDaysRemaining <= 7 ? 'bg-red-100' : 'bg-yellow-100'}`}>
              <svg className={`w-8 h-8 ${metrics.subscriptionDaysRemaining <= 7 ? 'text-red-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.subscription} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/subscription"
            className={`mt-4 inline-flex items-center font-medium text-sm transition-colors duration-200 ${
              metrics.subscriptionDaysRemaining <= 7 
                ? 'text-red-600 hover:text-red-800' 
                : 'text-yellow-600 hover:text-yellow-800'
            }`}
          >
            Gérer l'abonnement
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
      
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <ActivityChart data={activityTrends} />
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <UserDistributionChart data={userDistribution} />
        </div>
      </div>
      
      {/* User Engagement Chart */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <UserEngagementChart data={engagementData} />
        </div>
      </div>
      
      {/* Recent Activities Card */}
      <div className="bg-white p-6 rounded-xl shadow-sm mb-8 border border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Activités récentes</h2>
          </div>
          <div className="flex items-center space-x-3">
            <span className="bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">
              {recentActivities.length} activités
            </span>
          </div>
        </div>
        
        {recentActivities.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">Aucune activité récente à afficher</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Utilisateur</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivities.map((activity, index) => (
                  <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActivityBadgeColor(activity.actionType)}`}>
                        {activity.actionType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {(activity.userName || activity.userId || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.userName || activity.userId}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <Link 
            to="/admin/activities" 
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 inline-flex items-center font-medium text-sm transition-colors"
          >
            <span>Voir tous les logs d'activité</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>


    </div>
  );
};

export default AdminDashboard;